import { DotLottieWorker } from '@lottiefiles/dotlottie-web';
import { addWindowResizeEvent } from 'theme-utilities';

const fetchAnimationData = async (src) => {
	try {
		const response = await fetch(src);
		return await response.json();
	} catch (error) {
		console.error('Error fetching animation data', error);
		return false;
	}
};

export class Animation {
	constructor(canvas, src, options = {}) {
		const { autoplay = false, loop = false, triggerElement = '', onLoad = function () {}, pauseOnExit = true, workerId = 'default' } = options;
		this.canvas = canvas;
		this.src = src;

		// Options
		this.autoplay = autoplay;
		this.loop = loop;
		this.onLoad = onLoad;
		this.pauseOnExit = pauseOnExit;
		this.workerId = workerId;

		this.dotLottie = null;

		// Check for triggers
		if (triggerElement) {
			document.querySelectorAll(triggerElement).forEach((trigger) => {
				trigger.addEventListener('mouseenter', () => {
					this.play();
				});
			});
		}

		// Observe the canvas element
		this.intersectionObserverCallback = this.intersectionObserverCallback.bind(this);
		this.intersectionObserver = new IntersectionObserver(this.intersectionObserverCallback, {
			rootMargin: '400px',
		});
		this.intersectionObserver.observe(this.canvas);

		addWindowResizeEvent(() => this.resize());
	}

	initAnimation = async () => {
		const { data, aspectRatio } = await fetchAnimationData(this.src);

		// If data is false, unobserve the current entry and exit early
		if (!data) {
			this.intersectionObserver.unobserve(this.canvas);
			return;
		}

		// Set the aspect ratio
		if (aspectRatio) {
			this.canvas.style.setProperty('--aspect-ratio', aspectRatio);
			this.canvas.classList.add('known-aspect-ratio');
		}

		this.dotLottie = new DotLottieWorker({
			canvas: this.canvas,
			data,
			autoplay: this.autoplay,
			loop: this.loop,
			workerId: this.workerId,
			layout: {
				fit: 'contain',
				align: aspectRatio ? [0, 0] : [0.5, 0.5],
			},
		});

		// On load callback
		this.dotLottie.addEventListener('load', () => {
			this.canvas.classList.add('loaded');
			this.onLoad(this.dotLottie);
		});
	};

	intersectionObserverCallback(entries) {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				if (this.dotLottie === null) {
					this.initAnimation();
				} else if (this.autoplay) {
					this.dotLottie.play();
				}
			} else if (this.dotLottie !== null && this.autoplay && this.dotLottie.isPlaying && this.pauseOnExit) {
				this.dotLottie.pause();
			}
		});
	}

	play() {
		this.dotLottie?.play();
	}

	pause() {
		this.dotLottie?.pause();
	}

	stop() {
		this.dotLottie?.stop();
	}

	resize() {
		this.dotLottie?.resize();
	}
}
